<template>
  <div>
    <div class="job-list">
      <div class="job-item">
        <div class="left">
          <div class="position">
            <h2>{{ job.position }}</h2>
            <CommissionEarning
              v-if="showJobCommission"
              :incentive="job.incentive"
            />
          </div>
          <div v-if="showJobStatusSticker" class="job-sticker">
            <JobStatusSticker :stickers="job.stickers" />
          </div>
          <div class="type">
            <div v-if="job.jobIndustriesParentNames" class="industry">
              <font-awesome-icon :icon="['fas', 'building']" />
              <div class="item">
                <span>{{ job.jobIndustriesParentNames }}</span>
              </div>
            </div>
            <div v-if="job.jobCategoriesParentNames" class="category">
              <font-awesome-icon :icon="['fas', 'briefcase']" />
              <div class="item">
                <span>{{ job.jobCategoriesParentNames }}</span>
              </div>
            </div>
          </div>
          <div v-if="job.cityName" class="location">
            <i class="las la-map-marker"></i>
            <span>{{ job.cityName }}</span>
          </div>
          <div v-if="job.salary" class="salary">
            <i class="las la-money-bill"></i>
            {{ job.salary }}
          </div>
          <div v-if="showCandidateStatus" class="candidate-status">
            <CandidateProcessNumber :job="job" />
          </div>
          <div v-if="showPostDate" class="post-date">
            <i class="las la-undo-alt"></i>
            <span>{{ job.updatedAtTimeAgo }}</span>
          </div>
        </div>
        <div class="right">
          <div class="job-id">Job ID: {{ job.code }}</div>
        </div>
      </div>
    </div>

    <div v-if="!job.jobShareable" class="close-info">
      <p>Sorry, this job posting is now closed</p>
    </div>

    <div class="job-info">
      <ul class="list">
        <li class="position">
          <div class="heading">Job Title</div>
          <div class="info-text">{{ job.position }}</div>
        </li>
        <li v-if="job.cityName" class="location">
          <div class="heading">City</div>
          <div class="info-text">{{ job.cityName }}</div>
        </li>
        <li v-if="job.minSalary && job.maxSalary" class="salary">
          <div class="heading">Salary</div>
          <div class="info-text">
            {{ job.minSalary }} THB - {{ job.maxSalary }} THB
          </div>
        </li>
        <li v-if="job.companyName" class="company">
          <div class="heading">Company</div>
          <div class="info-text">{{ job.companyName }}</div>
        </li>
        <li v-if="job.companyUrl" class="company-website">
          <div class="heading">Company Website</div>
          <div class="info-text">
            <a :href="job.companyUrl" class="link" target="_blank">
              {{ job.companyUrl }}
            </a>
          </div>
        </li>
        <li v-if="job.companyProductAndService" class="product-and-service">
          <div class="heading">Product / Service</div>
          <div class="info-text">{{ job.companyProductAndService }}</div>
        </li>
        <!-- eslint-disable vue/no-v-html -->
        <li v-if="job.companyBusinessDetail" class="business-detail">
          <div class="heading">Business Details</div>
          <div class="info-text" v-html="job.companyBusinessDetail"></div>
        </li>
        <!-- eslint-enable vue/no-v-html -->
        <li v-if="jobCategoryName" class="category">
          <div class="heading">Job Category</div>
          <div class="info-text">
            {{ jobCategoryName }}
          </div>
        </li>
        <li v-if="jobIndustryName" class="company">
          <div class="heading">Industry</div>
          <div class="info-text">
            {{ jobIndustryName }}
          </div>
        </li>
        <!-- eslint-disable vue/no-v-html -->
        <li v-if="job.descriptionPublish" class="job-description">
          <div class="heading">Job Description</div>
          <div class="info-text" v-html="job.descriptionPublish"></div>
        </li>
        <li v-if="job.requirementPublish" class="requirement">
          <div class="heading">Requirement</div>
          <div class="info-text" v-html="job.requirementPublish"></div>
        </li>
        <li v-if="job.benefitPublish" class="benefit">
          <div class="heading">Benefit</div>
          <div class="info-text" v-html="job.benefitPublish"></div>
        </li>
        <li v-if="job.workHour" class="working-hour">
          <div class="heading">Working Hour</div>
          <div class="info-text">
            {{ job.workHour }}
          </div>
        </li>
        <li v-if="job.holiday" class="holiday">
          <div class="heading">Holiday</div>
          <div class="info-text" v-html="job.holiday"></div>
        </li>
        <li v-if="showJobKeyPoints" class="key-points">
          <div class="heading">Key Points by Reeracoen</div>
          <div class="info-text" v-html="job.keyPoint"></div>
        </li>
        <!-- eslint-enable vue/no-v-html -->
      </ul>
      <div class="job-recommended">
        <div class="left">
          <h3>Job Recommended by:</h3>
        </div>
        <div class="right">
          <p class="full-name">{{ user.fullName }}</p>
          <a class="email">{{ user.email }}</a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue'

import SuiVue from 'semantic-ui-vue'
Vue.use(SuiVue)

// Font Awesome
import { library } from '@fortawesome/fontawesome-svg-core'
import { faBuilding, faBriefcase } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'

library.add(faBuilding)
library.add(faBriefcase)

import JobStatusSticker from 'components/ui/JobStatusSticker.vue'
import CandidateProcessNumber from 'components/ui/CandidateProcessNumber.vue'
import CommissionEarning from 'components/ui/CommissionEarning.vue'

const ViewProps = window._application.props

export default {
  name: 'JobApplyDetail',

  components: {
    JobStatusSticker,
    CandidateProcessNumber,
    CommissionEarning,
    FontAwesomeIcon,
  },

  props: {
    job: {
      type: Object,
      required: true,
    },

    user: {
      type: Object,
      required: true,
    },

    token: {
      type: String,
      required: true,
    },

    showScrollButton: {
      type: Boolean,
      default: () => true,
    },

    showCandidateStatus: {
      type: Boolean,
      default: () => true,
    },

    showPostDate: {
      type: Boolean,
      default: () => true,
    },

    showJobCreatedAt: {
      type: Boolean,
      default: () => true,
    },

    showJobStatusSticker: {
      type: Boolean,
      default: () => true,
    },

    showJobKeyPoints: {
      type: Boolean,
      default: () => true,
    },

    showJobCommission: {
      type: Boolean,
      default: () => true,
    },
  },

  data() {
    return {
      csrfToken: ViewProps.csrfToken,
    }
  },

  computed: {
    jobCategoryName: function () {
      return this.job.jobCategories?.join(', ')
    },
    jobIndustryName: function () {
      return this.job.jobIndustries?.join(', ')
    },
  },
}
</script>

<style scoped lang="scss">
@import 'stylesheets/global';

.job-list {
  @include sp {
    padding: 0 10px;
  }
  .job-item {
    align-items: stretch;
    display: flex;
    flex-direction: row;
    margin-bottom: 1rem;
    padding: 0;
    width: 100%;

    @include sp {
      display: flex;
      flex-direction: column;
      margin-bottom: 1rem;
      padding: 0;
    }

    .left {
      flex: auto;
      width: 100%;

      .position {
        align-items: center;
        display: flex;
        margin-bottom: 0.5rem;
        width: fit-content;

        @include sp {
          display: block;
        }

        h2 {
          font-size: 1.25em;

          @include sp {
            font-size: 1em;
          }
        }
      }

      .location,
      .salary,
      .post-date {
        align-items: flex-start;
        display: flex;
        flex-direction: row;
        line-height: 1.75rem;
        margin-top: 2px;

        @include sp {
          line-height: 1.5rem;
        }

        i {
          color: $blue-primary;
          font-size: 20px;
          margin-right: 10px;
          margin-top: 3px;

          @include sp {
            font-size: 15px;
            margin-right: 8px;
            margin-top: 3px;
          }
        }

        span {
          color: $black;
          font-size: 1em;
          font-weight: 500;

          @include sp {
            font-size: 12px;
          }
        }
      }

      .salary {
        span {
          font-weight: 700;
        }
      }

      .post-date {
        i {
          color: $gray;
          font-size: 16px;
          margin-right: 10px;
          margin-top: 3px;

          @include sp {
            font-size: 14px;
            margin-right: 5px;
          }
        }

        i.la-undo-alt {
          font-size: 14px;
          margin-top: 4.5px;
        }

        span {
          color: $gray;
          font-size: 12px;
          font-weight: normal;

          @include sp {
            font-size: 10px;
          }
        }
      }

      .type {
        align-items: flex-start;
        display: flex;
        flex-direction: row;
        font-size: 14px;
        margin-top: 2px;
        padding-left: 3px;

        @include sp {
          display: grid;
          padding-left: 0;
        }

        svg {
          color: #6f6f6f;
          font-size: 14px;

          @include sp {
            font-size: 13px;
            margin: 2px 8px 0 3px;
          }
        }

        span {
          color: #6f6f6f;
          font-size: 1em;
          font-weight: 500;
          margin: 2px 4px;
          padding: 0 10px;

          @include sp {
            font-size: 12px;
            margin: 0;
            overflow: unset;
            padding: 0;
          }
        }

        .category,
        .industry {
          display: inline-flex;
          margin-right: 10px;
          padding-bottom: 5px;

          @include sp {
            margin-right: 0;
          }
        }
      }
    }

    .right {
      align-items: flex-end;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      width: 24%;

      @include sp {
        align-items: flex-start;
        display: flex;
        justify-content: space-between;
        margin-top: 5px;
        width: 100%;
      }

      .job-id {
        color: $gray;
        font-size: 1.15em;
        font-weight: 700;

        @include sp {
          font-size: 12px;
        }
      }
    }
  }
}

.close-info {
  align-items: center;
  background-color: #dfd8d8c6;
  display: flex;
  justify-content: center;
  margin-top: 50px;
  opacity: 0.7;
  padding: 20px;

  p {
    color: $black;
    font-size: 1.2em;
    font-weight: 500;
    text-align: center;

    @include sp {
      font-size: 1em;
    }
  }
}

.job-info {
  margin-top: 50px;

  @include sp {
    margin: 15px;
  }

  .list {
    li {
      margin-bottom: 1.5rem;

      .heading {
        color: $black;
        font-size: 1em;
        font-weight: 700;
        margin-bottom: 0.5rem;

        @include sp {
          font-size: 14px;
        }
      }

      .info-text {
        color: $black;
        line-height: 1.5rem;
        word-wrap: break-word;
        @include font-detail(1em, 400);

        @include sp {
          font-size: 14px;
        }

        .text-list {
          list-style-type: disc;
          padding-left: 1.2rem;

          li {
            line-height: 1.5rem;
            margin-bottom: 5px;
          }
        }
      }

      .link {
        color: $black;
        text-decoration: underline;

        &:hover,
        &:focus {
          color: $black;
        }
      }
    }
  }

  .job-recommended {
    border-top: 1px solid #c3c3c3;
    display: flex;
    flex-direction: row;
    padding-top: 20px;

    .left {
      flex: 1;
      font-size: 1.11rem;
      padding: 10px 0;
    }

    .right {
      display: flex;
      flex: 1;
      flex-direction: column;
      padding: 10px 0;

      .full-name {
        color: $black;
        font-size: 1.2em;
        font-weight: 500;
        margin-bottom: 0.5rem;

        @include sp {
          font-size: 1em;
        }
      }

      .email {
        color: $black;
        font-size: 1.05em;
        text-decoration: underline;

        @include sp {
          font-size: 13px;
        }
      }
    }
  }

  @include sp {
    .job-recommended {
      align-items: flex-start;
      flex-direction: column;
      font-size: 12px;
      padding: 0;

      .right {
        align-items: flex-start;
        padding: 10px 0;
      }
    }
  }
}
</style>
